import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import {
  type MenuItem,
  type ApiResponse,
  type ApiMenuItem,
  type GridMetadata,
  type GetGridMetadataParams,
  type MenuItemStructure,
  type GetMenuItemStructureParams,
  type ApiMenuItemStructure,
  mapApiMenuItemsToMenuItems,
  mapApiMenuItemStructure,
} from '../types';

const STRUCTURE_API = '/umbraco/api/structure';

/**
 * RTK Query API slice for menu-related endpoints
 */
export const menuApi = createApi({
  reducerPath: 'menuApi',
  baseQuery,
  tagTypes: ['MenuItems', 'GridMetadata', 'MenuItemStructure'],
  endpoints: (builder) => ({
    /**
     * Fetch menu items for navigation
     * Cached indefinitely until invalidated
     */
    getMenuItems: builder.query<MenuItem[], number | undefined>({
      query: (siteId) => ({
        url: `${STRUCTURE_API}/getmenuitems`,
        params: siteId ? { siteId } : {},
      }),
      transformResponse: (response: ApiResponse<ApiMenuItem[]>) => {
        if (response.code === 1) {
          return mapApiMenuItemsToMenuItems(response.data);
        }
        throw new Error(response.message || 'Failed to load menu items');
      },
      providesTags: (_result, _error, siteId) => [{ type: 'MenuItems', id: siteId || 'DEFAULT' }],
    }),

    /**
     * Fetch grid metadata (columns, data source config)
     * Cached per menuItemId or nodeKey
     */
    getGridMetadata: builder.query<GridMetadata, GetGridMetadataParams>({
      query: (params) => ({
        url: `${STRUCTURE_API}/getgridmetadata`,
        params,
      }),
      transformResponse: (response: ApiResponse<GridMetadata>) => {
        if (response.code === 1) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load grid metadata');
      },
      providesTags: (_result, _error, params) => [
        {
          type: 'GridMetadata',
          id: `${params.menuItemId || params.nodeKey || 'DEFAULT'}${params.parentId ? `:${params.parentId}` : ''}`,
        },
      ],
      keepUnusedDataFor: 300,
    }),

    /**
     * Fetch menu item structure (buttons, actions)
     * Cached per menuItemId
     */
    getMenuItemStructure: builder.query<MenuItemStructure, GetMenuItemStructureParams>({
      query: (params) => ({
        url: `${STRUCTURE_API}/getmenuitemstructure`,
        params,
      }),
      transformResponse: (response: ApiResponse<ApiMenuItemStructure>) => {
        if (response.code === 1) {
          return mapApiMenuItemStructure(response.data);
        }
        throw new Error(response.message || 'Failed to load menu item structure');
      },
      providesTags: (_result, _error, params) => [
        { type: 'MenuItemStructure', id: params.menuItemId },
      ],
      keepUnusedDataFor: Infinity,
    }),
  }),
});

export const { useGetMenuItemsQuery, useGetGridMetadataQuery, useGetMenuItemStructureQuery } =
  menuApi;

export const {
  endpoints: { getGridMetadata, getMenuItemStructure, getMenuItems },
  reducerPath,
  reducer,
  middleware,
} = menuApi;
